import React, { useState } from "react";
import { Link } from "react-router-dom";
import {
  Instagram as InstagramIcon,
} from "lucide-react";
import { FaXTwitter, FaWhatsapp, FaTiktok } from "react-icons/fa6";
import { BondLockup } from "./BondMark";

const COLORS = {
  primary: "#1E88E5",
  primaryHover: "#1976D2",
  primaryAlpha: {
    10: "rgba(30, 136, 229, 0.1)",
    20: "rgba(30, 136, 229, 0.2)",
    50: "rgba(30, 136, 229, 0.5)",
  },
};

const Footer = ({ onOpenHelpModal }) => {
  const [isHelpHovered, setIsHelpHovered] = useState(false);
  const [hoveredSocial, setHoveredSocial] = useState(null);

  const socialLinks = [
    {
      Icon: InstagramIcon,
      href: "https://www.instagram.com/betbondofficial/",
      label: "Instagram",
      hoverColor: COLORS.primary,
    },
    {
      Icon: FaXTwitter,
      href: "https://x.com/betbondofficial",
      label: "X",
      hoverColor: COLORS.primary,
    },
    {
      Icon: FaWhatsapp,
      href: "https://chat.whatsapp.com/LRLC4QIypkyBZ6uE2DZ31A?mode=gi_t",
      label: "WhatsApp",
      hoverColor: COLORS.primary,
    },
    {
      Icon: FaTiktok,
      href: "https://www.tiktok.com/@betbondofficial",
      label: "TikTok",
      hoverColor: COLORS.primary,
    },
  ];

  const footerLinkClass =
    "relative text-gray-400 hover:text-[#1E88E5] transition-all duration-300 group";

  return (
    <>
      <style>
        {`
          @keyframes slowBounce {
            0%, 100% {
              transform: translateY(0);
            }
            50% {
              transform: translateY(-8px);
            }
          }
          .animate-slow-bounce {
            animation: slowBounce 3s infinite ease-in-out;
          }
        `}
      </style>
      <button
        style={{
          backgroundColor: isHelpHovered ? COLORS.primaryHover : COLORS.primary,
          boxShadow: isHelpHovered
            ? `0 8px 24px ${COLORS.primaryAlpha[20]}`
            : `0 6px 18px ${COLORS.primaryAlpha[20]}`,
        }}
        className="
          fixed bottom-8 right-24 flex h-11 w-11 items-center justify-center
          rounded-full text-lg font-bold leading-none text-white
          transition-all duration-300 animate-slow-bounce hover:animate-none
          hover:brightness-110
        "
        onMouseEnter={() => setIsHelpHovered(true)}
        onMouseLeave={() => setIsHelpHovered(false)}
        onClick={onOpenHelpModal}
        type="button"
        aria-label="Need help"
      >
        ?
      </button>

      <footer className="bg-black w-full border-t border-white/[0.06]">
        <div className="container mx-auto flex flex-col md:flex-row justify-between items-center gap-4 px-4 py-8">
          <div className="flex flex-col items-center md:items-start gap-2">
            <BondLockup size={20} textClassName="text-lg" />
            <div className="text-[11px] text-gray-500 tracking-wide">
              © {new Date().getFullYear()} BetBond · Built in Rwanda · 18+ only
            </div>
            <p className="max-w-sm text-center md:text-left text-xs leading-relaxed text-gray-500">
              BETBOND LTD is a legally registered private company in Rwanda under
              the Office of the Registrar General.
            </p>
          </div>

          <div className="flex space-x-6 mb-4 md:mb-0">
            {socialLinks.map(({ Icon, href, label, hoverColor }) => (
              <a
                key={label}
                href={href}
                target="_blank"
                rel="noopener noreferrer"
                aria-label={label}
                className="group relative p-2"
                onMouseEnter={() => setHoveredSocial(label)}
                onMouseLeave={() => setHoveredSocial(null)}
              >
                <Icon
                  className="transition-all duration-300 transform"
                  style={{
                    color: hoveredSocial === label ? hoverColor : "#9CA3AF",
                    transform:
                      hoveredSocial === label ? "scale(1.2)" : "scale(1)",
                  }}
                  size={24}
                />
                <span
                  className="absolute -top-8 left-1/2 transform -translate-x-1/2 
                    bg-black/90 backdrop-blur-sm px-2 py-1 rounded text-xs
                    transition-all duration-300 border border-[#1E88E5]/20"
                  style={{
                    color: COLORS.primary,
                    opacity: hoveredSocial === label ? 1 : 0,
                    transform: `translate(-50%, ${
                      hoveredSocial === label ? "0" : "4px"
                    })`,
                  }}
                >
                  {label}
                </span>
              </a>
            ))}
          </div>

          <div className="flex flex-col items-center md:items-end gap-4">
            <div className="flex flex-wrap justify-center md:justify-end gap-x-8 gap-y-3 text-sm">
              <Link to="/app/p2p" className={footerLinkClass}>
                P2P
                <span
                  className="absolute -bottom-1 left-0 w-full h-0.5 transform scale-x-0 
                    group-hover:scale-x-100 transition-transform duration-300 origin-left"
                  style={{ backgroundColor: COLORS.primary }}
                />
              </Link>
              <Link to="/app/predictions" className={footerLinkClass}>
                Tips
                <span
                  className="absolute -bottom-1 left-0 w-full h-0.5 transform scale-x-0 
                    group-hover:scale-x-100 transition-transform duration-300 origin-left"
                  style={{ backgroundColor: COLORS.primary }}
                />
              </Link>
              <Link to="/download" className={footerLinkClass}>
                Download app
                <span
                  className="absolute -bottom-1 left-0 w-full h-0.5 transform scale-x-0 
                    group-hover:scale-x-100 transition-transform duration-300 origin-left"
                  style={{ backgroundColor: COLORS.primary }}
                />
              </Link>
              <Link to="/faq" className={footerLinkClass}>
                FAQ
                <span
                  className="absolute -bottom-1 left-0 w-full h-0.5 transform scale-x-0 
                    group-hover:scale-x-100 transition-transform duration-300 origin-left"
                  style={{ backgroundColor: COLORS.primary }}
                />
              </Link>
              <Link to="/terms" className={footerLinkClass}>
                Terms
                <span
                  className="absolute -bottom-1 left-0 w-full h-0.5 transform scale-x-0 
                    group-hover:scale-x-100 transition-transform duration-300 origin-left"
                  style={{ backgroundColor: COLORS.primary }}
                />
              </Link>
              <Link to="/privacy-policy" className={footerLinkClass}>
                Privacy Policy
                <span
                  className="absolute -bottom-1 left-0 w-full h-0.5 transform scale-x-0 
                    group-hover:scale-x-100 transition-transform duration-300 origin-left"
                  style={{ backgroundColor: COLORS.primary }}
                />
              </Link>
            </div>
            <a
              href="mailto:betbondofficial@gmail.com"
              className="text-sm text-gray-400 hover:text-[#1E88E5] transition-colors duration-300"
            >
              Support · betbondofficial@gmail.com
            </a>
          </div>
        </div>
      </footer>
    </>
  );
};

export default Footer;
